import { useGetAllJobPostsQuery } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import useSelectionOptions from '@/hooks/useSelectionOptions';
import { Calendar03Icon, ChevronDownIcon, SearchIcon } from '@/icons';
import { Button, DatePicker, Flex, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { useAppDiscloser, useAppFilters } from '../../../../../hooks';

const { RangePicker } = DatePicker;

type AssessmentsSegmentType = 'assessments-list' | 'assessments-scores';

type Props = { type: AssessmentsSegmentType };

const AssessmentsListFilters = () => {
  const { filters, handleFilter } = useAppFilters(FILTER_KEYS.ASSESS_LIST);
  const [_config, handleOpen] = useAppDiscloser(DISCLOSER_KEYS.ASSESS_LIST);
  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Select
          allowClear
          size="large"
          placeholder="Status"
          value={filters?.status}
          placement="bottomRight"
          suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
          onChange={(value) => handleFilter('status', value)}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      </Flex>
      <Button
        size="large"
        className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
        onClick={() => handleOpen({ type: 'ADD' })}
      >
        Create New Assessment
      </Button>
    </div>
  );
};

const AssessmentsScoresFilters = () => {
  const { filters, handleSearch, handleFilter } = useAppFilters(
    FILTER_KEYS.ASSESS_SCORES,
  );
  const [_config] = useAppDiscloser(DISCLOSER_KEYS.ASSESS_SCORES);

  const [jobOptions, isJobsFetching] = useSelectionOptions(
    useGetAllJobPostsQuery,
    { labelKey: 'job_title', valueKey: 'id' },
  );
  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Input
          allowClear
          size="large"
          className="!w-[250px] shrink-0"
          placeholder="Search Applicant"
          prefix={<SearchIcon size={20} />}
          onChange={handleSearch}
        />

        <div>
          <RangePicker
            allowClear
            size="large"
            value={
              filters?.start_date && filters?.end_date
                ? [dayjs(filters.start_date), dayjs(filters.end_date)]
                : null
            }
            prefix={<Calendar03Icon size={20} />}
            suffixIcon={null}
            placeholder={['Deadline Start', 'Deadline End']}
            onChange={(_, dateStrings) => {
              handleFilter('start_date', dateStrings[0]);
              handleFilter('end_date', dateStrings[1]);
            }}
          />
        </div>

        <div className="min-w-40">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            size="large"
            popupMatchSelectWidth={false}
            value={filters?.job_id}
            placement="bottomRight"
            placeholder="Job Position"
            options={jobOptions as any[]}
            loading={isJobsFetching as boolean}
            onChange={(value) => handleFilter('job_id', value)}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
          />
        </div>
        <div className="min-w-32">
          <Select
            allowClear
            size="large"
            placeholder="Status"
            value={filters?.status}
            placement="bottomRight"
            popupMatchSelectWidth={false}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(value) => handleFilter('status', value)}
            options={[
              { value: 'passed', label: 'Passed' },
              { value: 'failed', label: 'Failed' },
              { value: 'no_show', label: 'No-Show' },
              { value: 'pending', label: 'Pending' },
            ]}
          />
        </div>
      </Flex>

      <div className="h-[46px]" />
    </div>
  );
};

const Filters = ({ type }: Props) => {
  switch (type) {
    case 'assessments-list':
      return <AssessmentsListFilters />;
    case 'assessments-scores':
      return <AssessmentsScoresFilters />;
    default:
      return null;
  }
};
export default Filters;
